/**
 * TrendChart Component - Data visualization charts
 * Shows 7-day trends for cases, deaths, and vaccinations
 */

import { DistrictStats } from '@/api/ApiService';

interface ChartDataPoint {
  date: string;
  cases: number;
  deaths: number;
  vaccinated: number;
}

export class TrendChart {
  private container: HTMLElement | null = null;
  private chartData: ChartDataPoint[] = [];
  private selectedDistrict: string | null = null;
  private chartType: 'overview' | 'cases' | 'deaths' | 'vaccinations' = 'overview';

  render(container: HTMLElement): void {
    this.container = container;
    
    container.innerHTML = `
      <div class="chart-container">
        <div class="chart-header">
          <div class="chart-controls">
            <div class="chart-type-selector">
              <button class="chart-btn active" data-type="overview">📊 Overview</button>
              <button class="chart-btn" data-type="cases">🔴 Cases</button>
              <button class="chart-btn" data-type="deaths">⚰️ Deaths</button>
              <button class="chart-btn" data-type="vaccinations">💉 Vaccinations</button>
            </div>
            <div class="chart-period">
              <span class="period-label">Last 7 days</span>
            </div>
          </div>
        </div>
        
        <div class="chart-content">
          <div class="chart-area" id="chart-area">
            <!-- Chart will be rendered here -->
          </div>
          
          <div class="chart-legend" id="chart-legend">
            <!-- Legend will be rendered here -->
          </div>
        </div>
        
        <div class="chart-insights" id="chart-insights">
          <!-- Data insights will be shown here -->
        </div>
      </div>
    `;

    this.setupEventListeners();
    this.renderChart();
    this.addStyles();
  }

  private setupEventListeners(): void {
    if (!this.container) return;

    // Chart type selector
    this.container.querySelectorAll('.chart-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const type = target.getAttribute('data-type') as typeof this.chartType;
        if (type) {
          this.setChartType(type);
        }
      });
    });
  }

  private setChartType(type: typeof this.chartType): void {
    this.chartType = type;

    // Update button states
    this.container?.querySelectorAll('.chart-btn').forEach(btn => {
      btn.classList.remove('active');
      if (btn.getAttribute('data-type') === type) {
        btn.classList.add('active');
      }
    });

    // Re-render chart
    this.renderChart();
  }

  async updateData(districts: DistrictStats[], selectedDistrict?: string | null): Promise<void> {
    this.selectedDistrict = selectedDistrict || null;
    
    // Generate mock trend data (in real app, this would come from API)
    this.chartData = this.generateTrendData(districts, selectedDistrict);
    
    this.renderChart();
    this.updateInsights();
  }

  private generateTrendData(districts: DistrictStats[], selectedDistrict?: string | null): ChartDataPoint[] {
    // Generate last 7 days of data
    const data: ChartDataPoint[] = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      let totalCases = 0;
      let totalDeaths = 0;
      let totalVaccinated = 0;

      if (selectedDistrict) {
        const district = districts.find(d => d.district_code === selectedDistrict);
        if (district) {
          // Simulate daily data based on current totals
          const dayFactor = (7 - i) / 7;
          totalCases = Math.floor(district.total_cases * dayFactor);
          totalDeaths = Math.floor(district.deaths * dayFactor);
          totalVaccinated = Math.floor(district.vaccinated * dayFactor);
        }
      } else {
        // Aggregate all districts
        districts.forEach(district => {
          const dayFactor = (7 - i) / 7;
          totalCases += Math.floor(district.total_cases * dayFactor);
          totalDeaths += Math.floor(district.deaths * dayFactor);
          totalVaccinated += Math.floor(district.vaccinated * dayFactor);
        });
      }

      data.push({
        date: date.toISOString().split('T')[0],
        cases: totalCases,
        deaths: totalDeaths,
        vaccinated: totalVaccinated
      });
    }

    return data;
  }

  private renderChart(): void {
    const chartArea = this.container?.querySelector('#chart-area');
    if (!chartArea || this.chartData.length === 0) {
      this.showNoData();
      return;
    }

    switch (this.chartType) {
      case 'overview':
        this.renderOverviewChart(chartArea as HTMLElement);
        break;
      case 'cases':
        this.renderLineChart(chartArea as HTMLElement, 'cases', '#ef4444');
        break;
      case 'deaths':
        this.renderLineChart(chartArea as HTMLElement, 'deaths', '#7c2d12');
        break;
      case 'vaccinations':
        this.renderLineChart(chartArea as HTMLElement, 'vaccinated', '#10b981');
        break;
    }

    this.renderLegend();
  }

  private renderOverviewChart(container: HTMLElement): void {
    const maxValue = Math.max(
      ...this.chartData.map(d => Math.max(d.cases, d.deaths, d.vaccinated))
    );

    container.innerHTML = `
      <div class="svg-chart">
        <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg">
          <!-- Grid lines -->
          ${this.generateGridLines(400, 200, maxValue)}
          
          <!-- Data lines -->
          ${this.generateLine(this.chartData, 'cases', '#ef4444', 400, 200, maxValue)}
          ${this.generateLine(this.chartData, 'deaths', '#7c2d12', 400, 200, maxValue)}
          ${this.generateLine(this.chartData, 'vaccinated', '#10b981', 400, 200, maxValue)}
          
          <!-- Data points -->
          ${this.generatePoints(this.chartData, 'cases', '#ef4444', 400, 200, maxValue)}
          ${this.generatePoints(this.chartData, 'deaths', '#7c2d12', 400, 200, maxValue)}
          ${this.generatePoints(this.chartData, 'vaccinated', '#10b981', 400, 200, maxValue)}
          
          <!-- Axis labels -->
          ${this.generateAxisLabels(400, 200, maxValue)}
        </svg>
      </div>
      
      <div class="chart-x-axis">
        ${this.chartData.map((d, i) => `
          <div class="x-label">${this.formatDate(d.date)}</div>
        `).join('')}
      </div>
    `;
  }

  private renderLineChart(container: HTMLElement, dataKey: keyof ChartDataPoint, color: string): void {
    const data = this.chartData.map(d => d[dataKey] as number);
    const maxValue = Math.max(...data);

    container.innerHTML = `
      <div class="svg-chart">
        <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg">
          <!-- Grid lines -->
          ${this.generateGridLines(400, 200, maxValue)}
          
          <!-- Data line -->
          ${this.generateLine(this.chartData, dataKey, color, 400, 200, maxValue)}
          
          <!-- Data points -->
          ${this.generatePoints(this.chartData, dataKey, color, 400, 200, maxValue)}
          
          <!-- Area fill -->
          ${this.generateAreaFill(this.chartData, dataKey, color, 400, 200, maxValue)}
          
          <!-- Axis labels -->
          ${this.generateAxisLabels(400, 200, maxValue)}
        </svg>
      </div>
      
      <div class="chart-x-axis">
        ${this.chartData.map((d, i) => `
          <div class="x-label">${this.formatDate(d.date)}</div>
        `).join('')}
      </div>
    `;
  }

  private generateGridLines(width: number, height: number, maxValue: number): string {
    const lines = [];
    const step = height / 5;
    
    for (let i = 0; i <= 5; i++) {
      const y = height - (i * step);
      lines.push(`
        <line x1="0" y1="${y}" x2="${width}" y2="${y}" 
              stroke="#e2e8f0" stroke-width="0.5" opacity="0.5"/>
      `);
    }
    
    return lines.join('');
  }

  private generateLine(data: ChartDataPoint[], key: keyof ChartDataPoint, color: string, width: number, height: number, maxValue: number): string {
    if (data.length < 2) return '';

    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d[key] as number / maxValue) * height);
      return `${x},${y}`;
    }).join(' ');

    return `
      <polyline points="${points}" 
                fill="none" 
                stroke="${color}" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"/>
    `;
  }

  private generatePoints(data: ChartDataPoint[], key: keyof ChartDataPoint, color: string, width: number, height: number, maxValue: number): string {
    return data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d[key] as number / maxValue) * height);
      
      return `
        <circle cx="${x}" cy="${y}" r="3" 
                fill="${color}" 
                stroke="white" 
                stroke-width="2"
                class="chart-point"
                data-value="${d[key]}"
                data-date="${d.date}"/>
      `;
    }).join('');
  }

  private generateAreaFill(data: ChartDataPoint[], key: keyof ChartDataPoint, color: string, width: number, height: number, maxValue: number): string {
    if (data.length < 2) return '';

    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d[key] as number / maxValue) * height);
      return `${x},${y}`;
    });

    // Add bottom corners to close the area
    points.unshift(`0,${height}`);
    points.push(`${width},${height}`);

    return `
      <polygon points="${points.join(' ')}" 
               fill="${color}" 
               opacity="0.1"/>
    `;
  }

  private generateAxisLabels(width: number, height: number, maxValue: number): string {
    const labels = [];
    
    // Y-axis labels
    for (let i = 0; i <= 5; i++) {
      const value = (maxValue / 5) * i;
      const y = height - (i * (height / 5));
      
      labels.push(`
        <text x="-5" y="${y + 3}" 
              text-anchor="end" 
              font-size="10" 
              fill="#64748b">${Math.round(value)}</text>
      `);
    }
    
    return labels.join('');
  }

  private renderLegend(): void {
    const legendContainer = this.container?.querySelector('#chart-legend');
    if (!legendContainer) return;

    const legendItems = [];

    if (this.chartType === 'overview') {
      legendItems.push(
        { label: 'Cases', color: '#ef4444', value: this.chartData[this.chartData.length - 1]?.cases || 0 },
        { label: 'Deaths', color: '#7c2d12', value: this.chartData[this.chartData.length - 1]?.deaths || 0 },
        { label: 'Vaccinated', color: '#10b981', value: this.chartData[this.chartData.length - 1]?.vaccinated || 0 }
      );
    } else {
      const key = this.chartType === 'vaccinations' ? 'vaccinated' : this.chartType;
      const colors = {
        cases: '#ef4444',
        deaths: '#7c2d12',
        vaccinated: '#10b981'
      };
      
      legendItems.push({
        label: this.chartType.charAt(0).toUpperCase() + this.chartType.slice(1),
        color: colors[key as keyof typeof colors],
        value: this.chartData[this.chartData.length - 1]?.[key] || 0
      });
    }

    legendContainer.innerHTML = `
      <div class="legend-items">
        ${legendItems.map(item => `
          <div class="legend-item">
            <div class="legend-indicator" style="background-color: ${item.color}"></div>
            <span class="legend-label">${item.label}</span>
            <span class="legend-value">${item.value.toLocaleString()}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  private updateInsights(): void {
    const insightsContainer = this.container?.querySelector('#chart-insights');
    if (!insightsContainer || this.chartData.length < 2) return;

    const latest = this.chartData[this.chartData.length - 1];
    const previous = this.chartData[this.chartData.length - 2];

    const casesTrend = this.calculateTrend(previous.cases, latest.cases);
    const deathsTrend = this.calculateTrend(previous.deaths, latest.deaths);
    const vaccineTrend = this.calculateTrend(previous.vaccinated, latest.vaccinated);

    const districtText = this.selectedDistrict 
      ? `in ${this.selectedDistrict} District` 
      : 'across all districts';

    insightsContainer.innerHTML = `
      <div class="insights-header">
        <h3>Key Insights ${districtText}</h3>
      </div>
      <div class="insights-grid">
        <div class="insight-item">
          <div class="insight-icon">📈</div>
          <div class="insight-content">
            <div class="insight-title">Cases Trend</div>
            <div class="insight-description">
              ${casesTrend.direction} ${casesTrend.percentage}% from yesterday
            </div>
          </div>
        </div>
        
        <div class="insight-item">
          <div class="insight-icon">⚰️</div>
          <div class="insight-content">
            <div class="insight-title">Deaths Trend</div>
            <div class="insight-description">
              ${deathsTrend.direction} ${deathsTrend.percentage}% from yesterday
            </div>
          </div>
        </div>
        
        <div class="insight-item">
          <div class="insight-icon">💉</div>
          <div class="insight-content">
            <div class="insight-title">Vaccination Progress</div>
            <div class="insight-description">
              ${vaccineTrend.direction} ${vaccineTrend.percentage}% from yesterday
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private calculateTrend(previous: number, current: number): { direction: string; percentage: number } {
    const change = current - previous;
    const percentage = previous > 0 ? Math.abs((change / previous) * 100) : 0;
    
    return {
      direction: change > 0 ? '↗️ Increased' : change < 0 ? '↘️ Decreased' : '→ Stable',
      percentage: Math.round(percentage)
    };
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      month: 'short', 
      day: 'numeric' 
    });
  }

  private showNoData(): void {
    const chartArea = this.container?.querySelector('#chart-area');
    if (chartArea) {
      chartArea.innerHTML = `
        <div class="no-data">
          <div class="no-data-icon">📊</div>
          <div class="no-data-text">No trend data available</div>
          <div class="no-data-subtitle">Chart will update when data is loaded</div>
        </div>
      `;
    }
  }

  showLoading(): void {
    if (!this.container) return;

    const chartArea = this.container.querySelector('#chart-area');
    if (chartArea) {
      chartArea.innerHTML = `
        <div class="chart-loading">
          <div class="loading-skeleton chart-skeleton"></div>
          <div class="loading-text">Loading chart data...</div>
        </div>
      `;
    }
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .chart-container {
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow);
        overflow: hidden;
      }
      
      .chart-header {
        padding: var(--space-4);
        border-bottom: 1px solid var(--gray-200);
        background: var(--gray-50);
      }
      
      .chart-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing-4);
      }
      
      @media (max-width: 768px) {
        .chart-controls {
          flex-direction: column;
          gap: var(--spacing-3);
        }
      }
      
      .chart-type-selector {
        display: flex;
        gap: var(--spacing-1);
        background: white;
        border-radius: var(--radius);
        padding: var(--spacing-1);
        border: 1px solid var(--gray-200);
      }
      
      @media (max-width: 768px) {
        .chart-type-selector {
          flex-wrap: wrap;
          justify-content: center;
        }
      }
      
      .chart-btn {
        padding: var(--spacing-2) var(--spacing-3);
        border: none;
        background: transparent;
        border-radius: var(--radius-sm);
        font-size: var(--text-sm);
        cursor: pointer;
        transition: var(--transition-base);
        white-space: nowrap;
      }
      
      .chart-btn:hover {
        background: var(--gray-100);
      }
      
      .chart-btn.active {
        background: var(--primary-green);
        color: white;
      }
      
      .period-label {
        font-size: var(--font-size-sm);
        color: var(--gray-600);
        font-weight: 500;
      }
      
      .chart-content {
        padding: var(--spacing-6);
      }
      
      .chart-area {
        margin-bottom: var(--spacing-6);
        min-height: 250px;
      }
      
      .svg-chart {
        width: 100%;
        height: 200px;
        margin-bottom: var(--spacing-4);
      }
      
      .svg-chart svg {
        width: 100%;
        height: 100%;
      }
      
      .chart-point {
        cursor: pointer;
        transition: r 0.2s ease;
      }
      
      .chart-point:hover {
        r: 5;
      }
      
      .chart-x-axis {
        display: flex;
        justify-content: space-between;
        font-size: var(--font-size-xs);
        color: var(--gray-600);
        padding: 0 var(--spacing-2);
      }
      
      .x-label {
        text-align: center;
        min-width: 50px;
      }
      
      .chart-legend {
        padding: var(--spacing-4);
        background: var(--gray-50);
        border-radius: var(--radius);
      }
      
      .legend-items {
        display: flex;
        gap: var(--spacing-6);
        justify-content: center;
      }
      
      @media (max-width: 768px) {
        .legend-items {
          flex-direction: column;
          gap: var(--spacing-3);
        }
      }
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-2);
        font-size: var(--font-size-sm);
      }
      
      .legend-indicator {
        width: 12px;
        height: 12px;
        border-radius: 50%;
      }
      
      .legend-label {
        color: var(--gray-700);
      }
      
      .legend-value {
        font-weight: 600;
        color: var(--gray-900);
      }
      
      .chart-insights {
        padding: var(--spacing-6);
        border-top: 1px solid var(--gray-200);
        background: var(--gray-50);
      }
      
      .insights-header h3 {
        margin-bottom: var(--spacing-4);
        color: var(--primary-color);
        text-align: center;
      }
      
      .insights-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--spacing-4);
      }
      
      @media (min-width: 768px) {
        .insights-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      .insight-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-3);
        padding: var(--spacing-3);
        background: white;
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
      }
      
      .insight-icon {
        font-size: var(--font-size-lg);
        flex-shrink: 0;
      }
      
      .insight-content {
        flex: 1;
      }
      
      .insight-title {
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-1);
      }
      
      .insight-description {
        font-size: var(--font-size-sm);
        color: var(--gray-600);
      }
      
      .no-data {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        gap: var(--spacing-3);
      }
      
      .no-data-icon {
        font-size: 2rem;
        opacity: 0.5;
      }
      
      .no-data-text {
        font-weight: 600;
        color: var(--gray-600);
      }
      
      .no-data-subtitle {
        font-size: var(--font-size-sm);
        color: var(--gray-500);
      }
      
      .chart-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        gap: var(--spacing-4);
      }
      
      .chart-skeleton {
        width: 100%;
        height: 150px;
        border-radius: var(--radius);
      }
      
      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        .chart-btn,
        .chart-point {
          transition: none;
        }
      }
      
      /* High contrast mode */
      @media (prefers-contrast: high) {
        .chart-btn {
          border: 1px solid var(--gray-400);
        }
        
        .chart-btn.active {
          border-color: var(--primary-color);
        }
        
        .svg-chart line {
          stroke-width: 1;
        }
      }
    `;
    
    document.head.appendChild(style);
  }
}